import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Blob } from "@/components/Blob";
import { resolveImage } from "@/lib/api";

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.85, delay: 0.25 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Hero = ({ heroImage }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const blobY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  const headlineLines = ["Cold-Pressed.", "Nothing Added.", "Just Juice."];

  return (
    <section id="top" ref={ref} className="relative overflow-hidden pt-28 pb-16 sm:pt-36 sm:pb-24" data-testid="hero-section">
      {/* background blobs */}
      <motion.div style={{ y: blobY }}>
        <Blob color="#FFC93C" size={520} className="opacity-30 -top-32 -left-40 animate-float-slow" />
        <Blob color="#FF3D68" size={360} className="opacity-25 top-40 -right-24 animate-float-slower" />
        <Blob color="#7ED321" size={300} className="opacity-20 bottom-0 left-1/4 animate-float-slow" />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-10 lg:gap-6 items-center">
        {/* Left */}
        <div>
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="inline-flex items-center gap-2 rounded-full bg-[#7ED321]/15 border border-[#7ED321]/30 px-4 py-1.5 mb-6"
          >
            <span className="w-2 h-2 rounded-full bg-[#7ED321] animate-pulse" />
            <span className="text-[13px] font-bold text-[#5A9E12] tracking-wide uppercase">Fresh batch every morning</span>
          </motion.div>

          <h1 className="font-display font-extrabold tracking-tight leading-[0.95] text-[#1A1A1A] text-5xl sm:text-6xl lg:text-7xl">
            {headlineLines.map((line, i) => (
              <span className="reveal-line" key={line}>
                <motion.span
                  className="block"
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="visible"
                  style={{
                    color: i === 2 ? "#FF7A1A" : "#1A1A1A",
                  }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="mt-6 text-lg text-[#1A1A1A]/70 max-w-md leading-relaxed"
          >
            Small-batch, cold-pressed juices made from real fruit and veg. No concentrates, no sugar, no shortcuts — bottled at peak freshness in Holly's kitchen.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 1.05 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <a
              href="#juices"
              className="inline-flex items-center gap-2 rounded-full bg-[#FF7A1A] hover:bg-[#FFC93C] hover:text-[#1A1A1A] text-white font-bold text-base px-8 py-4 transition-all duration-300 shadow-[0_12px_30px_rgba(255,122,26,0.4)] active:scale-95"
              data-testid="explore-juices-btn"
            >
              Explore Fresh Juices
              <span aria-hidden>↓</span>
            </a>
            <a href="#recipes" className="text-base font-bold text-[#1A1A1A] underline decoration-[#FFC93C] decoration-4 underline-offset-4 hover:decoration-[#FF3D68] transition-colors" data-testid="hero-recipes-link">
              Free recipes
            </a>
          </motion.div>
        </div>

        {/* Right — spotlight image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9, rotate: 3 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <div className="absolute inset-0 -m-6 rounded-[40px] bg-gradient-to-br from-[#FFC93C]/50 via-[#FF7A1A]/20 to-[#FF3D68]/30 blur-2xl" />
          <motion.div
            style={{ y: imgY }}
            className="relative rounded-[32px] overflow-hidden border-4 border-white shadow-[0_30px_60px_rgba(255,122,26,0.25)] aspect-[4/5] max-w-md mx-auto"
          >
            <img
              src={resolveImage(heroImage) || "https://images.unsplash.com/photo-1621506289937-a8e4df240d0b?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"}
              alt="Fresh cold-pressed juice"
              className="w-full h-full object-cover"
              data-testid="hero-image"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1.2, type: "spring", stiffness: 200 }}
            className="absolute -bottom-4 -left-2 sm:left-6 bg-white rounded-2xl px-5 py-3 shadow-[0_12px_30px_rgba(26,26,26,0.12)] rotate-[-4deg]"
          >
            <p className="font-display font-extrabold text-[#FF7A1A] text-2xl leading-none">100%</p>
            <p className="text-xs font-semibold text-[#1A1A1A]/60">Real fruit & veg</p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
